import { useAuth, useResendCountdown } from "@babackapp/auth-client/hooks";
import {
  AuthError,
  type MfaEmailCodeSent,
  type MfaMethod,
} from "@babackapp/auth-client/types";
import type { UserDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { Label } from "@babackapp/ui/components/label";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";

type ChallengeMode = MfaMethod | "recovery";

const SWITCH_LABEL_KEYS: Record<ChallengeMode, string> = {
  totp: "signin.mfa.useAuthenticator",
  email: "signin.mfa.useEmail",
  recovery: "signin.mfa.useRecoveryCode",
};

interface MfaChallengeFormProps {
  mfaToken: string;
  /** The default method first. */
  methods: MfaMethod[];
  onVerified: (user: UserDto) => void;
  /** The challenge has expired: the password has to be typed again. */
  onRestart: () => void;
}

export default function MfaChallengeForm({
  mfaToken,
  methods,
  onVerified,
  onRestart,
}: MfaChallengeFormProps) {
  const t = useTranslation();
  const auth = useAuth();
  const [mode, setMode] = useState<ChallengeMode>(methods[0] ?? "totp");
  const [value, setValue] = useState("");
  const [emailSent, setEmailSent] = useState<MfaEmailCodeSent | null>(null);
  const [isSending, setIsSending] = useState(false);
  const emailRequestedFor = useRef<string | null>(null);
  const secondsBeforeResend = useResendCountdown(emailSent);

  const restartIfChallengeExpired = useCallback(
    (error: unknown) => {
      if (
        error instanceof AuthError &&
        error.code === "AUTH_MFA_INVALID_CHALLENGE"
      ) {
        onRestart();
      }
    },
    [onRestart],
  );

  const { sendMfaEmailCode } = auth;
  const sendEmailCode = useCallback(async () => {
    setIsSending(true);
    try {
      setEmailSent(await sendMfaEmailCode(mfaToken));
    } catch (error) {
      restartIfChallengeExpired(error);
    } finally {
      setIsSending(false);
    }
  }, [sendMfaEmailCode, mfaToken, restartIfChallengeExpired]);

  // The first time the email mode shows, once per challenge: effects run twice
  // in development, and a second send would hit the resend delay.
  useEffect(() => {
    if (mode !== "email" || emailRequestedFor.current === mfaToken) {
      return;
    }
    emailRequestedFor.current = mfaToken;
    void sendEmailCode();
  }, [mode, mfaToken, sendEmailCode]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      const user = await auth.verifyMfa(
        mode === "recovery"
          ? { mfaToken, recoveryCode: value }
          : mode === "email"
            ? { mfaToken, emailCode: value }
            : { mfaToken, code: value },
      );
      onVerified(user);
    } catch (error) {
      setValue("");
      restartIfChallengeExpired(error);
    }
  };

  const switchMode = (nextMode: ChallengeMode) => {
    setMode(nextMode);
    setValue("");
  };

  // Recovery codes belong to the authenticator app.
  const alternatives = [
    ...methods,
    ...(methods.includes("totp") ? (["recovery"] as const) : []),
  ].filter((alternative) => alternative !== mode);

  const isRecoveryMode = mode === "recovery";
  const description =
    mode === "totp"
      ? t("signin.mfa.codeDescription")
      : mode === "recovery"
        ? t("signin.mfa.recoveryDescription")
        : emailSent
          ? t("mfa.email.codeSent", { email: emailSent.maskedEmail })
          : t("signin.mfa.emailDescription");

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-1">
        <h3 className="text-lg font-medium text-gray-900">
          {t("signin.mfa.title")}
        </h3>
        <p className="text-sm text-gray-600">{description}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="mfa-code">
          {mode === "totp"
            ? t("signin.mfa.codeLabel")
            : mode === "recovery"
              ? t("signin.mfa.recoveryLabel")
              : t("mfa.email.codeLabel")}
        </Label>
        {isRecoveryMode ? (
          <Input
            id="mfa-code"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            autoComplete="off"
            placeholder="xxxxx-xxxxx"
            autoFocus
          />
        ) : (
          <Input
            id="mfa-code"
            value={value}
            onChange={(event) =>
              setValue(event.target.value.replace(/\D/g, "").slice(0, 6))
            }
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            autoFocus
          />
        )}
        {mode === "email" && (
          <button
            type="button"
            onClick={() => void sendEmailCode()}
            disabled={isSending || secondsBeforeResend > 0}
            className="text-sm font-medium text-purple-600 hover:text-purple-500 disabled:text-gray-400"
          >
            {secondsBeforeResend > 0
              ? t("mfa.email.resendIn", { seconds: secondsBeforeResend })
              : t("mfa.email.resend")}
          </button>
        )}
      </div>

      <Button
        className="w-full"
        type="submit"
        disabled={auth.isLoading || (!isRecoveryMode && value.length !== 6)}
      >
        {t("signin.mfa.verify")}
      </Button>

      <div className="flex flex-col items-center gap-2 text-sm">
        {alternatives.map((alternative) => (
          <button
            key={alternative}
            type="button"
            onClick={() => switchMode(alternative)}
            className="font-medium text-purple-600 hover:text-purple-500"
          >
            {t(SWITCH_LABEL_KEYS[alternative])}
          </button>
        ))}
        <button
          type="button"
          onClick={onRestart}
          className="text-gray-600 hover:text-gray-900"
        >
          {t("signin.mfa.back")}
        </button>
      </div>
    </form>
  );
}
